import { Component, inject, input, output, OnInit } from '@angular/core';
import { LinkSchema } from '../../../data/schemas/link.schema';
import { Router, RouterLink, RouterLinkActive, RouterOutlet, NavigationEnd, NavigationError } from '@angular/router';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-ongletlayout',
  imports: [RouterOutlet, RouterLinkActive, RouterLink],
  templateUrl: './ongletlayout.component.html',
  styleUrls: ['./ongletlayout.component.css']
})
export class OngletlayoutComponent implements OnInit {
  public title = input<string>();
  public ongletLinks = input.required<LinkSchema[]>();
  public onClickMenu = output<void>();

  // Identifiant unique du groupe d'onglets — obligatoire pour éviter les collisions
  // Exemple : storageId="parametres" pour le niveau 1, storageId="parametres-marches" pour le niveau 2
  public storageId = input.required<string>();

  private router = inject(Router);

  private get storageKey(): string {
    const userId = this.getCurrentUserId();
    return `last-tab:${userId}:${this.storageId()}`;
  }

  private getCurrentUserId(): string {
    return localStorage.getItem('userId') ?? 'anonymous';
  }

  ngOnInit(): void {
    this.router.events
      .pipe(filter(e => e instanceof NavigationError))
      .subscribe(() => {
        this.clearSavedTab();
        this.navigateToFirstAccessibleTab();
      });

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        if (this.urlBelongsToOurTabs(e.urlAfterRedirects)) {
          this.saveTab(e.urlAfterRedirects);
        }
      });

    this.restoreLastTab();
  }

  private restoreLastTab(): void {
    const links = this.ongletLinks();
    if (!links?.length) return;

    const currentUrl = this.router.url;
    const savedUrl = this.getSavedTab();

    if (!savedUrl) return;

    if (!this.urlBelongsToOurTabs(savedUrl)) {
      this.clearSavedTab();
      return;
    }

    const isOnParentRoute = links.every(link => !link.link || !currentUrl.includes(link.link));
    if (!isOnParentRoute) return;

    if (!this.isSafeUrl(savedUrl)) {
      this.clearSavedTab();
      return;
    }

    this.router.navigateByUrl(savedUrl, { replaceUrl: true }).then(success => {
      if (!success) {
        this.clearSavedTab();
        this.navigateToFirstAccessibleTab();
      }
    });
  }

  private urlBelongsToOurTabs(url: string): boolean {
    return this.ongletLinks().some(link =>
      link.link ? url.includes(link.link) : this.ongletLinks().every(l => !l.link || !url.includes(l.link))
    );
  }

  private isSafeUrl(url: string): boolean {
    if (!url.startsWith('/')) return false;
    if (/^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(url)) return false;
    if (url.includes('..')) return false;
    return true;
  }

  private navigateToFirstAccessibleTab(): void {
    const links = this.ongletLinks();
    if (links?.length) {
      this.router.navigate([links[0].link], { replaceUrl: true });
    }
  }

  private saveTab(url: string): void {
    try { localStorage.setItem(this.storageKey, url); } catch {}
  }

  private getSavedTab(): string | null {
    try { return localStorage.getItem(this.storageKey); } catch { return null; }
  }

  private clearSavedTab(): void {
    try { localStorage.removeItem(this.storageKey); } catch {}
  }

  isActive(link: LinkSchema): boolean {
    const exact = link.link === '';
    return exact
      ? this.router.isActive(link.link, { paths: 'exact', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' })
      : this.router.isActive(link.link, { paths: 'subset', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' });
  }

  saveAndNavigate(): void {
    this.onClickMenu.emit();
  }
}